import type { Step } from './types';

import trReasons from './i18n/stepReasons/tr.json';
import enReasons from './i18n/stepReasons/en.json';
import deReasons from './i18n/stepReasons/de.json';
import esReasons from './i18n/stepReasons/es.json';
import frReasons from './i18n/stepReasons/fr.json';
import itReasons from './i18n/stepReasons/it.json';
import nlReasons from './i18n/stepReasons/nl.json';
import plReasons from './i18n/stepReasons/pl.json';
import ruReasons from './i18n/stepReasons/ru.json';

type ReasonMap = Record<string, string>;

const REASON_TEMPLATES: Record<string, ReasonMap> = {
  tr: trReasons,
  en: enReasons,
  de: deReasons,
  es: esReasons,
  fr: frReasons,
  it: itReasons,
  nl: nlReasons,
  pl: plReasons,
  ru: ruReasons,
};

// SudokuWiki notasyonu: Satırlar A-J (I atlanır), Sütunlar 1-9 — backend/solver.py:_ROW_LETTERS ile birebir.
const ROW_LETTERS = 'ABCDEFGHJ';

const UNIT_WORDS: Record<string, { row: string; col: string; box: string }> = {
  tr: { row: 'Satır', col: 'Sütun', box: 'Kutu' },
  en: { row: 'Row', col: 'Col', box: 'Box' },
  de: { row: 'Zeile', col: 'Spalte', box: 'Box' },
  es: { row: 'Fila', col: 'Col', box: 'Caja' },
  fr: { row: 'Ligne', col: 'Col', box: 'Bloc' },
  it: { row: 'Riga', col: 'Col', box: 'Riquadro' },
  nl: { row: 'Rij', col: 'Kol', box: 'Vak' },
  pl: { row: 'Wiersz', col: 'Kol', box: 'Blok' },
  ru: { row: 'Строка', col: 'Кол', box: 'Блок' },
};

function rowLabel(index: number): string {
  return ROW_LETTERS[index] ?? String(index + 1);
}

function colLabel(index: number): string {
  return String(index + 1);
}

function boxLabel(index: number, locale: string): string {
  const words = UNIT_WORDS[locale] ?? UNIT_WORDS.en;
  return `${words.box} ${index + 1}`;
}

function cellLabel(row: number, col: number): string {
  return `${rowLabel(row)}${col + 1}`;
}

interface UnitRef {
  type: 'row' | 'col' | 'box';
  index: number;
}

function formatUnit(unit: UnitRef, locale: string): string {
  const words = UNIT_WORDS[locale] ?? UNIT_WORDS.en;
  if (unit.type === 'row') return `${words.row} ${rowLabel(unit.index)}`;
  if (unit.type === 'col') return `${words.col} ${colLabel(unit.index)}`;
  return boxLabel(unit.index, locale);
}

function formatUnitList(indices: number[], type: 'row' | 'col' | 'box', locale: string): string {
  return indices.map((i) => formatUnit({ type, index: i }, locale)).join(', ');
}

function formatDigitSet(digits: number[]): string {
  return `{${digits.join(', ')}}`;
}

function formatList(items: string[]): string {
  return items.join(', ');
}

// Bu alanlar matematiksel "{a,b}" aday kümesi notasyonunu temsil ediyor (orijinal Türkçe
// f-string'lerdeki {a},{b} gibi). uncolored_digits/remaining_digits/elim_digits ise düz
// virgülle ayrılmış bir liste (orijinalinde parantezsiz ','.join) — küme notasyonu DEĞİL.
const DIGIT_SET_KEYS = new Set(['digits', 'pivot_digits', 'base_digits']);

function isChainPairArray(v: unknown): v is [string, number][] {
  return Array.isArray(v) && v.length > 0 && Array.isArray(v[0])
    && typeof (v[0] as unknown[])[0] === 'string' && typeof (v[0] as unknown[])[1] === 'number';
}

function isCellListArray(v: unknown): v is string[][] {
  return Array.isArray(v) && v.length > 0 && Array.isArray(v[0]) && typeof (v[0] as unknown[])[0] === 'string' && !isChainPairArray(v);
}

/** reason_params'ı locale'e göre düz {placeholder: string} haritasına çevirir. */
function buildParams(params: Record<string, unknown>, locale: string): Record<string, string> {
  const out: Record<string, string> = {};

  // Aday hücre bazlı zincirler: [["A1", 3], ["B2", 3], ...] → "A1(3) → B2(3)"
  if (isChainPairArray(params.chain)) {
    out.chain = (params.chain as [string, number][]).map(([c, d]) => `${c}(${d})`).join(' → ');
  } else if (Array.isArray(params.chain) && typeof params.chain[0] === 'string') {
    out.chain = formatList(params.chain as string[]);
  }

  // Death Blossom leaves: [["A1","B2"], ["C3"]] → "{A1, B2}; {C3}"
  if (isCellListArray(params.leaves)) {
    out.leaves = (params.leaves as string[][]).map((cells) => `{${cells.join(', ')}}`).join('; ');
  }

  for (const [key, value] of Object.entries(params)) {
    if (key === 'chain' || key === 'leaves') continue;
    if (key === 'box' && typeof value === 'number') {
      out[key] = boxLabel(value, locale);
    } else if (key === 'unit' && value && typeof value === 'object') {
      out[key] = formatUnit(value as UnitRef, locale);
    } else if (key === 'line' && value && typeof value === 'object') {
      out[key] = formatUnit(value as UnitRef, locale);
    } else if (key === 'unit_type' && typeof value === 'string') {
      const words = UNIT_WORDS[locale] ?? UNIT_WORDS.en;
      out[key] = words[value as 'row' | 'col' | 'box'] ?? value;
    } else if (typeof value === 'number') {
      out[key] = String(value);
    } else if (typeof value === 'string') {
      out[key] = value;
    } else if (Array.isArray(value) && value.every((v) => typeof v === 'number')) {
      out[key] = DIGIT_SET_KEYS.has(key) ? formatDigitSet(value as number[]) : formatList((value as number[]).map(String));
    } else if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      out[key] = formatList(value as string[]);
    }
  }

  if (Array.isArray(params.digits) && (params.digits as unknown[]).every((v) => typeof v === 'number')) {
    const digits = params.digits as number[];
    out.digit_list = formatList(digits.map(String));
    if (digits.length >= 1) out.digit1 = String(digits[0]);
    if (digits.length >= 2) out.digit2 = String(digits[1]);
    if (digits.length >= 3) out.digit3 = String(digits[2]);
  }

  // Balık teknikleri (X-Wing/Swordfish/Jellyfish/Finned): base/cover, variant'a göre satır/sütun.
  if (Array.isArray(params.base) && Array.isArray(params.cover) && typeof params.variant === 'string'
    && (params.base as unknown[]).every((v) => typeof v === 'number')) {
    const baseType = params.variant === 'row' ? 'row' : 'col';
    const coverType = params.variant === 'row' ? 'col' : 'row';
    out.base = formatUnitList(params.base as number[], baseType, locale);
    out.cover = formatUnitList(params.cover as number[], coverType, locale);
  }

  return out;
}

function interpolate(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    return Object.prototype.hasOwnProperty.call(params, key) ? params[key] : match;
  });
}

/**
 * Bir solver adımının açıklamasını verilen dilde üretir.
 * reason_key/reason_params yoksa veya o dil için şablon eksikse backend'in
 * ürettiği Türkçe `reason` metnine (fallback) düşer.
 */
export function explainStep(step: Step, locale: string): string {
  if (!step.reason_key) return step.reason;
  const templates = REASON_TEMPLATES[locale] ?? REASON_TEMPLATES.en;
  const template = templates?.[step.reason_key] ?? REASON_TEMPLATES.tr[step.reason_key];
  if (!template) return step.reason;
  const params = buildParams(step.reason_params ?? {}, locale);
  if (step.row >= 0 && step.col >= 0) {
    params.cell = cellLabel(step.row, step.col);
  }
  return interpolate(template, params);
}
